import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  debounce,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ILegalTransaction } from '../Interfaces/ILegalTransaction';

import { TabContext, TabList, TabPanel } from '@mui/lab';
import { isEqual } from 'lodash';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { NATURAL_PERSON_CLIENT_TYPE_ID } from '../constants';
import { useAlert } from '../context/AlertContext';
import { showConfirmDialogComponent } from '../core/ConfirmDialog';
import { LoadingDialog } from '../core/LoadingDialog';
import { NoDataDialog } from '../core/NoDataDialog';
import {
  ILegalClient,
  ILegalClientFullObject,
  initLegalClient,
  legalClientFullObjectSchema,
  transferLegalClientInput,
} from '../Interfaces/ILegalClient';
import { ILegalClientRelationship } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationship';
import { legalClientRelationshipInputSchema } from '../Interfaces/ILegalClientRelationship/ILegalClientRelationshipInput';
import { AddressTab } from './AddressTab/AddressTab';
import { AddArnotopSyncDialog } from './Arnotop/AddArnotopSyncDialog';
import { BankInformationTab } from './BankInformationTab/BankInformationTab';
import { ContactInformationTab } from './ContactInformationTab/ContactInformationTab';
import { LegalClientTypeForm } from './LegalClientTypeForm';
import { LegalClientUserForm } from './LegalClientUserForm';
import { LegalPersonForm } from './LegalPersonForm';
import { LegalTransactionsTab } from './LegalTransactionsTab/LegalTransactionsTab';
import { NaturalPersonForm } from './NaturalPersonForm';
import { RelationshipsTab } from './RelationsTab/RelationshipTab';
import { useCreateRelationshipFullObject } from '../hooks/reactQuerys/RelationshipQueries';
import {
  useCheckEmail,
  useCreateLegalClientFullObject,
  useGetLegalClientContactInformationTypes,
  useGetLegalClientTypes,
} from '../hooks/reactQuerys/LegalClientQueries';
import { Business, OpenInNew, Person } from '@mui/icons-material';

interface IProps {
  onSave?: (idLegalClient: number) => void;
  unmount?: () => void;
  onlyJuristic?: boolean; //nur juristrische
}

export const CreateLegalClientDialog = ({
  onSave,
  unmount,
  onlyJuristic,
}: IProps) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [formValues, setFormValues] = useState<ILegalClientFullObject>(
    initLegalClient(onlyJuristic !== undefined && onlyJuristic === true)
  );

  // Prefill aus Query-Parametern — wird von den Plugins (Outlook,
  // Thunderbird) genutzt, um den Dialog mit den bereits bekannten Daten
  // einer E-Mail zu öffnen (Email/FirstName/LastName aus dem Mail-Header).
  // Läuft nur einmal beim Mount; Edits danach dürfen vom User kommen.
  useEffect(() => {
    const email = searchParams.get('Email') || searchParams.get('email');
    const firstName =
      searchParams.get('FirstName') || searchParams.get('firstName');
    const lastName =
      searchParams.get('LastName') || searchParams.get('lastName');
    if (!email && !firstName && !lastName) return;
    setFormValues(prev => ({
      ...prev,
      ...(email ? { Email: email } : {}),
      ...(firstName ? { FirstName: firstName } : {}),
      ...(lastName ? { LastName: lastName } : {}),
    }));
    // Tab "Kontaktdaten" automatisch öffnen, damit der User direkt sieht
    // was vorbefüllt wurde — eine Aktion weniger.
  }, []);
  const [selectedLegalTransactions, setSelectedLegalTransactions] = useState<
    ILegalTransaction[]
  >([]);
  const [relationships, setRelationships] = useState<
    ILegalClientRelationship[]
  >([]);

  const [isOpenArnoTopSync, setIsOpenArnoTopSync] = useState(false);

  const [currentTab, setCurrentTab] = useState('1');

  const { showAlert } = useAlert();

  const { checkEmail, isPendingCheckEmail, abortCheckMail } = useCheckEmail();
  const [sameEmailAdressList, setSameEmailAdressList] = useState<
    ILegalClient[]
  >([]);
  const [isDebouncing, setIsDebouncing] = useState(false);

  const {
    contactInformationTypes,
    isLoadingContactInformationTypes,
    refetchContactInformationTypes,
  } = useGetLegalClientContactInformationTypes();

  const {
    legalClientTypes,
    isLoadingLegalClientTypes,
    refetchLegalClientTypes,
  } = useGetLegalClientTypes();

  const {
    createRelationshipFullObject,
    isPendingCreateRelationshipFullObject,
  } = useCreateRelationshipFullObject();

  const formRef = useRef<HTMLFormElement>(null);

  const { createLegalClientFullObject, isPendingCreateLegalClientFullObject } =
    useCreateLegalClientFullObject();

  useEffect(() => {
    if (!selectedLegalTransactions) return;
    setFormValues(prev => ({
      ...prev,
      LegalTransactionArray: selectedLegalTransactions.map(lt => ({
        LegalTransaction: lt,
        LegalRole: null,
        idLegalRole: null,
      })),
    }));
  }, [selectedLegalTransactions]);

  const onCheckEmail = async (email: string) => {
    const checkList = await checkEmail({ Email: email });
    setSameEmailAdressList(checkList);
  };

  const debouncedSearch = useMemo(
    () =>
      debounce((testString: string) => {
        if (testString !== '') {
          abortCheckMail();
          onCheckEmail(testString);
        } else {
          setSameEmailAdressList([]);
        }
        setFormValues((prev: ILegalClientFullObject) => ({
          ...prev,
          Email: testString,

          ...(!testString || prev.idLegalClientType !== 1
            ? {
                passwordResetRequired: false,
                resetPassword: false,
                isReceivingEmailEnabled: false,
                loginPossible: false,
              }
            : {
                passwordResetRequired: true,
                resetPassword: false,
                isReceivingEmailEnabled: false,
                loginPossible: true,
              }),
        }));

        setIsDebouncing(false);
      }, 700),
    []
  );

  const onCreate = async (legalClient: ILegalClientFullObject = formValues) => {
    try {
      const input = transferLegalClientInput(legalClient);

      const { success, data } = legalClientFullObjectSchema.safeParse(input);

      if (
        !success ||
        (formValues.idLegalClientType === 1 && !formValues.FirstName) // Manuel validation of FirstName because it is not required in the schema but should be required in the form
      ) {
        showAlert({
          text: 'Bitte füllen Sie alle Felder aus',
          severity: 'error',
        });
        return;
      }

      const { idLegalClient }: ILegalClientFullObject =
        await createLegalClientFullObject(data);

      await handleRelationships(idLegalClient);

      showAlert({ text: 'Mandant erfolgreich erstellt' });

      if (onSave) onSave(idLegalClient);
      else navigate(`/clients/${idLegalClient}`);
    } catch {
      showAlert({
        text: 'Fehler beim Erstellen des Mandanten',
        severity: 'error',
      });
    }
  };

  const handleRelationships = async (newLegalClientId: number) => {
    const tasks: Promise<void>[] = relationships.map(r =>
      createRelationshipFullObject(
        legalClientRelationshipInputSchema.parse({
          ...r,
          Members: r.Members.map(m => ({
            ...m,
            idLegalClient:
              m.idLegalClient > 0 ? m.idLegalClient : newLegalClientId,
          })),
        })
      )
    );
    await Promise.all(tasks);
  };

  const wrapperHandleCreateLegalClient = () => {
    if (formValues.syncWithArnoTop) {
      setIsOpenArnoTopSync(true);
    } else {
      onCreate();
    }
  };

  const closeMethod = () => (unmount ? unmount() : navigate('/clients'));

  const handleConfirmedClose = async () => {
    if (isPending) return;
    if (isEdited) {
      const isConfirm = await showConfirmDialogComponent(
        'Warnung',
        'Sind Sie sicher, dass Sie den Mandanten ohne Speichern schließen wollen?',
        'Schließen',
        'Abbrechen',
        theme
      );
      if (isConfirm) closeMethod();
    } else {
      closeMethod();
    }
  };

  /**
   * Form Function
   */
  const handleOnSave = () => {
    if (formRef.current && formRef.current.checkValidity()) {
      onCreate();
    } else if (formRef.current && !formRef.current.checkValidity()) {
      const elements = Array.from(formRef.current.elements).reverse();

      elements.forEach((element: any) => {
        if (element.reportValidity()) {
          element.reportValidity();
        }
      });
    }
  };

  const refetchAll = () => {
    refetchContactInformationTypes();
    refetchLegalClientTypes();
  };

  const isDuplicateEmail = sameEmailAdressList.length > 0;

  const isMainFormEdited = !isEqual(formValues, initLegalClient());
  const isRelationshipsFormEdited = relationships.length > 0;

  const isEdited = isMainFormEdited || isRelationshipsFormEdited;

  const isPending =
    isPendingCreateRelationshipFullObject ||
    isPendingCreateLegalClientFullObject;

  if (isLoadingContactInformationTypes || isLoadingLegalClientTypes)
    return <LoadingDialog unmount={closeMethod} />;
  if (contactInformationTypes === undefined || legalClientTypes === undefined)
    return <NoDataDialog unmount={closeMethod} refetch={refetchAll} />;

  return (
    <>
      <Dialog
        open
        onClose={handleConfirmedClose}
        maxWidth='lg'
        fullWidth
        scroll='paper'
      >
        {isPending && (
          <LinearProgress
            sx={{
              height: '4px',
            }}
          />
        )}
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 3,
            py: 2,
          }}
        >
          <Typography variant='h5'>Neuer Mandant</Typography>
        </Box>
        <DialogContent sx={{ minHeight: '70vh' }}>
          <Collapse in={isDuplicateEmail}>
            <Alert severity='warning' sx={{ mb: 3 }}>
              <AlertTitle>Warnung: E-Mail-Adresse</AlertTitle>
              Diese E-Mail-Adresse ist bereits bei folgenden Mandanten
              hinterlegt:
              <Stack spacing={1.5} sx={{ mt: 2 }}>
                {sameEmailAdressList
                  .filter(lc => lc.idLegalClient !== formValues.idLegalClient)
                  .map(lc => (
                    <Paper
                      component='a'
                      href={`/clients/${lc.idLegalClient}`}
                      target='_blank'
                      elevation={0}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all .15s',
                        '&:hover': {
                          borderColor: 'warning.main',
                          backgroundColor: 'action.hover',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      {lc.idBusinessForm !== 1 ? <Business /> : <Person />}

                      <Box sx={{ flexGrow: 1 }}>
                        {lc.idBusinessForm === 1 && lc.FirstName} {lc.LastName}
                      </Box>

                      <OpenInNew fontSize='small' color='action' />
                    </Paper>
                  ))}
              </Stack>
            </Alert>
          </Collapse>

          <Grid container spacing={2}>
            <form ref={formRef}>
              <Grid container item spacing={2} pt={1}>
                <Grid item xs={3}>
                  <LegalClientTypeForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                    onlyJuristic={onlyJuristic}
                  />
                </Grid>
                {formValues.idLegalClientType ===
                NATURAL_PERSON_CLIENT_TYPE_ID ? (
                  <NaturalPersonForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                ) : (
                  <LegalPersonForm
                    formValues={formValues}
                    setFormValues={setFormValues}
                  />
                )}
                <Grid item xs={12}>
                  <Box sx={{ mt: 0.5 }} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='E-Mail-Adresse'
                    size='small'
                    fullWidth
                    type='email'
                    onChange={({
                      target,
                    }: React.ChangeEvent<HTMLInputElement>) => {
                      setIsDebouncing(true);
                      debouncedSearch(target.value);
                    }}
                  />
                </Grid>
              </Grid>
            </form>

            <LegalClientUserForm
              formValues={formValues}
              setFormValues={setFormValues}
            />

            {/*
            <ArnoTopSyncForm
              legalClient={formValues}
              onCheckedChange={checked => {
                if (checked) {
                  setFormValues({
                    ...formValues,
                    syncWithArnoTop: true,
                    syncType: 1,
                  });
                  setIsOpenArnoTopSync(true);
                } else {
                  setFormValues({
                    ...formValues,
                    syncWithArnoTop: false,
                    syncType: undefined,
                    idArnoTop: undefined,
                  });
                }
              }}
            />
             */}
          </Grid>
          <TabContext value={currentTab}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 4 }}>
              <TabList onChange={(_, newValue) => setCurrentTab(newValue)}>
                <Tab label='Addressen' value='1' />
                <Tab label='Kontakt' value='2' />
                <Tab label='Rechtsgeschäfte' value='3' />
                <Tab label='Beziehungen' value='4' />
                <Tab label='Bankinformationen' value='5' />
                <Tooltip title='Noch nicht verfügbar'>
                  <Box>
                    <Tab label='Gesprächsprotokolle' value='6' disabled />
                  </Box>
                </Tooltip>
                <Tooltip title='Noch nicht verfügbar'>
                  <Box>
                    <Tab label='Dokumente' value='7' disabled />
                  </Box>
                </Tooltip>
              </TabList>
            </Box>
            <TabPanel value='1'>
              <AddressTab
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>
            <TabPanel value='2'>
              <ContactInformationTab
                contactInformationTypes={contactInformationTypes!}
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>
            <TabPanel value='3'>
              <LegalTransactionsTab
                legalTransactions={selectedLegalTransactions}
                setLegalTransactions={setSelectedLegalTransactions}
              />
            </TabPanel>
            <TabPanel value='4'>
              <RelationshipsTab
                relationships={relationships}
                setRelationships={setRelationships}
              />
            </TabPanel>

            <TabPanel value='5'>
              <BankInformationTab
                formValues={formValues}
                setFormValues={setFormValues}
              />
            </TabPanel>
            <TabPanel value='6'>
              <Typography variant='h5'>Gesprächsprotokolle</Typography>
            </TabPanel>
          </TabContext>
        </DialogContent>
        <DialogActions
          sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
        >
          <Button variant='outlined' onClick={handleConfirmedClose}>
            Abbrechen
          </Button>
          <Button
            variant='contained'
            disabled={
              isPending ||
              isDebouncing ||
              isPendingCheckEmail ||
              isDuplicateEmail ||
              !isEdited
            }
            onClick={handleOnSave}
          >
            {isPending ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Mandant erstellen</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Mandant erstellen'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {isOpenArnoTopSync && (
        <AddArnotopSyncDialog
          legalClient={formValues}
          onSave={arnoTopSyncState => {
            setFormValues({
              ...formValues,
              syncWithArnoTop: arnoTopSyncState.syncWithArnoTop,
              syncType: arnoTopSyncState.syncType,
              idArnoTop: arnoTopSyncState.idArnoTop,
            });
            setIsOpenArnoTopSync(false);
          }}
        />
      )}
    </>
  );
};
